/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  FilterNames,
  FilterRestService,
  jobCsCreateDialogRoute,
} from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermission,
  getAelEventsArray,
  JobStatus,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { JobListFilter, JobListItem } from '../../resources/job.model';
import { JobNavigationService } from '../../resources/job-navigation.service';
import { filter$ } from '@gh/rx/operators';
import { JobCsListBusinessService } from '../../resources/job-cs-list-business.service';
import { ID } from '@gh/common-shared-data';

@Component({
  standalone: false,
   selector: 'gh-job-color-selection-list',
   templateUrl: 'job-color-selection-list.component.html',
 })
export class JobColorSelectionListComponent implements OnInit {
  dataSource: SlicedDataSource<JobListItem>;
  form: FilterableFormState<JobListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();

  get havePermissions(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private builderUserContext: BuilderUserContext,
              private formStateBuilder: FormStateBuilder,
              private angRouter: Router,
              private businessService: JobCsListBusinessService,
              private notifications: NotificationService,
              private router: AppRouter,
              private filterService: FilterRestService,
              private jobNavigation: JobNavigationService,
              private navigationService: BuilderNavigationService,
              public gridDefinitionFactory: GridDefinitionFactory,
              private dialogService: DialogService) {

  }

  get canCreateJobCs(): boolean {
    return this.builderUserContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.builderUserContext.hasSystemRole(SystemRoleBit.Master) ||
      this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT);
  }

  isJobDpp(job: JobListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout, createCS, message} = getRouteInitData(this.route);
    if (message) {
      this.notifications.error(message);
    }

    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        specUnresolvedChanges: {
          cellClass: 'text-center',
        },
        csMissingDetails: {
          cellClass: 'text-center',
        },
        csUnresolvedChanges: {
          cellClass: 'text-center',
        },
        specProducedDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        status: {
          minWidth: 120,
          cellClass: ({data}: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
            [JobStatus.Finalized]: 'finalized-status',
          }[data.statusValue]),
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id)
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showFinalized: void 0,
        showDpp: void 0,
        showActive: void 0,
        showUserAssignedJobs: void 0,
        showNotProceeding: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.dataSource = this.businessService.findAllAsDataSource({
         ...filter,
       });
      if (!isInit) {
        this.filterService.update(FilterNames.jobCsList, filter).subscribe();
      }
      isInit = false;
    });
    if (createCS) {
      this.onJobCsCreate(createCS);
    }
    const queryParams: Params = {createCS: '', message: ''};
    this.angRouter.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      },
    );
  }

  onJobCsCreate(jobId?: ID): void {
    this.dialogService.openWithInput(jobCsCreateDialogRoute, {
      isFromCreateBtn: !jobId,
      jobId,
    } as any)
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((formValue: any) => {
        this.router.navigateToChild(
          ['job-admin/jobs', formValue.jobId, 'color-selection', 'new'],
          {
            queryParams: { csMasterId: formValue.master.id },
            parentTitle: 'lbl_color_selections',
          });
      });
  }

  onJobDetailsOpen(job: JobListItem): void {
    this.jobNavigation.onJobDetailsOpen(job, 'lbl_color_selections');
  }

  goToAel(job?: JobListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_color_selections',
      entityType: 'JOB',
      entityId: job ? job.id : undefined,
      eventTypeIds: getAelEventsArray(AelEvents.Job.ColorSelections),
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onJobSpecificationOpen(job: JobListItem): void {
    this.jobNavigation.onJobSpecificationOpen(job, 'lbl_color_selections');
  }

  onColorSelectionOpen(job: JobListItem): void {
    this.jobNavigation.onColorSelectionOpen(job, 'lbl_color_selections');
  }

  onJobEstimateOpen(job: JobListItem): void {
    this.jobNavigation.onJobEstimateOpen(job);
  }

  onFinancialsOpen(job: JobListItem): void {
    this.jobNavigation.onFinancialsOpen(job);
  }

  onJobScheduleOpen(job: JobListItem): void {
    // not implemented yet
  }

  onJobOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobOrderOpen(job, 'lbl_color_selections');
  }

  onJobChangeOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobChangeOrderOpen(job, 'lbl_color_selections');
  }
}
